import React, { useEffect, useState } from 'react';
import { ActivityButton } from './ActivityButton';
import { FieldSetModal } from './FieldSetModal';
import { NumericFieldSetModal } from './NumericFieldSetModal';
import { ScoreWidget, ScoreField } from './ScoreWidget';
import { cn } from '../primitives';

// GraphQL response types
export interface DiaryField {
  id: string;
  name: string;
  icon: string;
  score: number;
  type: 'toggle' | 'numeric' | 'unknown';
  fieldSetId: string | null;
  position: number;
  hasGoal: boolean;
  minValue: number | null;
  maxValue: number | null;
  stepValue: number | null;
  units: string | null;
}

export interface DiaryFieldSet {
  id: string;
  name: string;
  position: number;
  categoryId: string;
  hasGoal: boolean;
  fields: DiaryField[];
}

export interface NumericFieldSetLabel {
  id: string;
  name: string;
  color: string;
  value: number;
  position: number;
}

export interface DiaryNumericFieldSet {
  id: string;
  name: string;
  icon: string;
  position: number;
  categoryId: string;
  units: string;
  minValue: number;
  maxValue: number;
  labels: NumericFieldSetLabel[];
}

export interface DiaryCategory {
  id: string;
  name: string;
  position: number;
  fields: DiaryField[];
  fieldSets: DiaryFieldSet[];
  numericFieldSets: DiaryNumericFieldSet[];
}

export interface DiaryActivity {
  id: string;
  active: boolean;
  value: number | null;
  fieldId: string | null;
  numericFieldSetId: string | null;
  automaticallyTriggered: boolean;
}

export interface DiaryDay {
  id: string;
  date: string;
  mood: number | null;
  energy: number | null;
  note: string | null;
  activities: DiaryActivity[];
}

export interface DiaryScoreWidget {
  id: string;
  name: string;
  position: number;
  scoreFields: ScoreField[];
}

export interface DiaryDayPageProps {
  /** Current date in ISO format (YYYY-MM-DD) */
  date: string;
  /** Diary day data */
  diaryDay: DiaryDay;
  /** Categories with fields */
  categories: DiaryCategory[];
  /** Score widgets */
  scoreWidgets?: DiaryScoreWidget[];
  /** Callback when a toggle/fieldset field is clicked */
  onFieldClick: (fieldId: string) => void;
  /** Callback when a fieldset field is clicked with specific field to activate */
  onFieldSetClick: (fieldSetId: string, fieldId?: string) => void;
  /** Callback when numeric field value changes */
  onNumericFieldChange: (fieldId: string, value: number, action: 'set_value' | 'plus' | 'minus') => void;
  /** Callback when numeric field set is clicked (cycles to next label) */
  onNumericFieldSetClick?: (numericFieldSetId: string) => void;
  /** Callback when numeric field set value is set via slider or label */
  onNumericFieldSetValueChange?: (numericFieldSetId: string, value: number, labelId?: string) => void;
  /** Callback when score widget field value changes */
  onScoreWidgetFieldChange?: (fieldId: string, value: number) => void;
  /** Callback when note changes */
  onNoteChange?: (note: string) => void;
  /** Whether the page is loading */
  isLoading?: boolean;
  /** Custom class name */
  className?: string;
  /** Icon base URL for rendering icons */
  iconBaseUrl?: string;
}

/**
 * DiaryDayPage - Main diary day page component
 *
 * Renders categories with toggle, numeric, and fieldset activities
 * Uses ActivityButton component for all field types
 */
export function DiaryDayPage({
  date,
  diaryDay,
  categories,
  scoreWidgets = [],
  onFieldClick,
  onFieldSetClick,
  onNumericFieldChange,
  onNumericFieldSetClick,
  onNumericFieldSetValueChange,
  onScoreWidgetFieldChange,
  onNoteChange,
  isLoading = false,
  className,
  iconBaseUrl = '/assets',
}: DiaryDayPageProps) {
  // Modal state for field set selection
  const [modalFieldSet, setModalFieldSet] = useState<DiaryFieldSet | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Modal state for numeric field set
  const [modalNumericFieldSet, setModalNumericFieldSet] = useState<DiaryNumericFieldSet | null>(null);
  const [isNumericModalOpen, setIsNumericModalOpen] = useState(false);
  // Note editing state
  const [noteValue, setNoteValue] = useState(diaryDay.note || '');

  // Sync note value with diaryDay.note when it changes externally
  useEffect(() => {
    setNoteValue(diaryDay.note || '');
  }, [diaryDay.note]);

  // Debounced note save
  useEffect(() => {
    if (!onNoteChange) return;
    if (noteValue === (diaryDay.note || '')) return;

    const timeout = setTimeout(() => {
      onNoteChange(noteValue);
    }, 500);

    return () => clearTimeout(timeout);
  }, [noteValue, onNoteChange, diaryDay.note]);

  // Create a map of activities by field ID for quick lookup
  const activitiesByFieldId = React.useMemo(() => {
    const map = new Map<string, DiaryActivity>();
    diaryDay.activities.forEach(activity => {
      if (activity.fieldId) {
        map.set(activity.fieldId, activity);
      }
    });
    return map;
  }, [diaryDay.activities]);

  // Create a map of activities by numeric field set ID for quick lookup
  const activitiesByNumericFieldSetId = React.useMemo(() => {
    const map = new Map<string, DiaryActivity>();
    diaryDay.activities.forEach(activity => {
      if (activity.numericFieldSetId) {
        map.set(activity.numericFieldSetId, activity);
      }
    });
    return map;
  }, [diaryDay.activities]);

  // Get activity for a field
  const getActivity = (fieldId: string): DiaryActivity | undefined => {
    return activitiesByFieldId.get(fieldId);
  };

  // Get activity for a numeric field set
  const getNumericFieldSetActivity = (numericFieldSetId: string): DiaryActivity | undefined => {
    return activitiesByNumericFieldSetId.get(numericFieldSetId);
  };

  // Render a single field
  const renderField = (field: DiaryField) => {
    const activity = getActivity(field.id);
    const iconUrl = field.icon;

    if (field.type === 'numeric') {
      const value = activity?.value || 0;
      const targetValue = field.maxValue || 100;
      const formattedValue = field.units ? `${value} ${field.units}` : `${value}`;

      return (
        <ActivityButton
          key={`field-${field.id}`}
          id={field.id}
          name={field.name}
          icon={iconUrl}
          variant="numeric"
          active={activity?.active || false}
          automated={activity?.automaticallyTriggered || false}
          score={field.score}
          hasGoal={field.hasGoal}
          value={value}
          targetValue={targetValue}
          formattedValue={formattedValue}
          onClick={() => {
            // For numeric fields, clicking could open a modal/popover
            // For now, we just increment by step value
            const newValue = value + (field.stepValue || 1);
            if (field.maxValue && newValue <= field.maxValue) {
              onNumericFieldChange(field.id, newValue, 'set_value');
            }
          }}
        />
      );
    }

    // Toggle field
    return (
      <ActivityButton
        key={`field-${field.id}`}
        id={field.id}
        name={field.name}
        icon={iconUrl}
        variant="toggle"
        active={activity?.active || false}
        automated={activity?.automaticallyTriggered || false}
        score={field.score}
        hasGoal={field.hasGoal}
        onClick={() => onFieldClick(field.id)}
      />
    );
  };

  // Render a field set
  const renderFieldSet = (fieldSet: DiaryFieldSet) => {
    // Find which field in the set is active (if any)
    const activeField = fieldSet.fields.find(f => getActivity(f.id)?.active);
    const visibleField = activeField || fieldSet.fields[0];

    if (!visibleField) return null;

    const activity = getActivity(visibleField.id);
    const iconUrl = visibleField.icon;

    // Get secondary field scores for display
    const secondaryFields = fieldSet.fields
      .filter(f => f.id !== visibleField.id)
      .slice(0, 2)
      .map(f => ({ score: f.score }));

    // Check if the field set is incomplete (has no fields at all)
    const incomplete = fieldSet.fields.length === 0;

    return (
      <ActivityButton
        key={`fieldset-${fieldSet.id}`}
        id={fieldSet.id}
        name={visibleField.name}
        icon={iconUrl}
        variant="fieldset"
        active={activity?.active || false}
        automated={activity?.automaticallyTriggered || false}
        score={visibleField.score}
        hasGoal={visibleField.hasGoal}
        incomplete={incomplete}
        secondaryFields={secondaryFields}
        onClick={() => {
          // If there's only one field or already active, just toggle the field set
          if (fieldSet.fields.length === 1 || activeField) {
            onFieldSetClick(fieldSet.id);
          } else {
            // Open popover to select which field to activate
            // For now, we'll activate the first field
            onFieldSetClick(fieldSet.id, fieldSet.fields[0]?.id);
          }
        }}
        onLongPress={() => {
          // Only show modal if there are multiple fields (more than 1)
          if (fieldSet.fields.length > 1) {
            setModalFieldSet(fieldSet);
            setIsModalOpen(true);
          }
        }}
      />
    );
  };

  // Render a numeric field set
  const renderNumericFieldSet = (numericFieldSet: DiaryNumericFieldSet) => {
    const activity = getNumericFieldSetActivity(numericFieldSet.id);
    const currentValue = activity?.value;
    
    // Find the matching label for the current value, if any
    const matchingLabel = currentValue !== null && currentValue !== undefined
      ? numericFieldSet.labels.find(l => l.value === currentValue)
      : null;
    
    // Display: use the matching label name, or show percentage if intermediate value
    const visibleLabel = matchingLabel || numericFieldSet.labels[0];
    const isIntermediateValue = currentValue !== null && currentValue !== undefined && !matchingLabel;
    
    // Determine display text
    let displayName = numericFieldSet.name;
    if (activity?.active && currentValue !== null && currentValue !== undefined) {
      if (isIntermediateValue) {
        displayName = `${numericFieldSet.name}: ${currentValue}${numericFieldSet.units === 'percentage' ? '%' : ''}`;
      } else if (matchingLabel) {
        displayName = `${numericFieldSet.name}: ${matchingLabel.name}`;
      }
    }

    // Get color based on value (use label color if matching, otherwise calculate from value)
    const getColorForValue = () => {
      if (!activity?.active || currentValue === null || currentValue === undefined) {
        return '#fff';
      }
      if (matchingLabel) {
        return matchingLabel.color;
      }
      // For intermediate values, calculate color based on position in range
      const percentage = ((currentValue - numericFieldSet.minValue) / (numericFieldSet.maxValue - numericFieldSet.minValue)) * 100;
      // Simple gradient from red to green
      const r = Math.round(255 * (1 - percentage / 100));
      const g = Math.round(255 * (percentage / 100));
      return `rgb(${r}, ${g}, 150)`;
    };

    return (
      <ActivityButton
        key={`numericfieldset-${numericFieldSet.id}`}
        id={numericFieldSet.id}
        name={displayName}
        icon={numericFieldSet.icon}
        variant="fieldset"
        active={activity?.active || false}
        automated={activity?.automaticallyTriggered || false}
        score={currentValue !== null && currentValue !== undefined ? currentValue : undefined}
        hasGoal={false}
        incomplete={numericFieldSet.labels.length < 2}
        secondaryFields={numericFieldSet.labels.slice(1, 3).map(l => ({ score: l.value }))}
        onClick={() => {
          // Cycle to next label
          if (onNumericFieldSetClick) {
            onNumericFieldSetClick(numericFieldSet.id);
          }
        }}
        onLongPress={() => {
          // Show modal with all labels and slider
          setModalNumericFieldSet(numericFieldSet);
          setIsNumericModalOpen(true);
        }}
      />
    );
  };

  // Render a category
  const renderCategory = (category: DiaryCategory) => {
    // Combine fields, field sets, and numeric field sets, sorted by position
    const items: Array<DiaryField | DiaryFieldSet | DiaryNumericFieldSet> = [
      ...category.fields,
      ...category.fieldSets,
      ...(category.numericFieldSets || []),
    ].sort((a, b) => a.position - b.position);

    if (items.length === 0) return null;

    return (
      <div
        key={category.id}
        className={cn('card shadow-sm diary-category-card mb-3')}
        data-category-id={category.id}
      >
        <div className="card-header">
          <h5 className="card-title mb-0">{category.name}</h5>
        </div>
        <div className="card-body px-2">
          <div className="d-flex flex-wrap gap-1">
            {items.map(item => {
              if ('labels' in item) {
                // It's a numeric field set
                return renderNumericFieldSet(item as DiaryNumericFieldSet);
              } else if ('fields' in item) {
                // It's a field set
                return renderFieldSet(item as DiaryFieldSet);
              } else {
                // It's a field
                return renderField(item as DiaryField);
              }
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className={cn('diary-day-page', className)} data-date={date}>
        <div className="diary-categories-container">
          {categories
            .sort((a, b) => a.position - b.position)
            .map(category => renderCategory(category))}
        </div>

        {/* Score widgets */}
        {scoreWidgets.length > 0 && onScoreWidgetFieldChange && (
          <div className="diary-score-widgets-container">
            {scoreWidgets
              .sort((a, b) => a.position - b.position)
              .map(widget => {
                // Build field values map from activities
                const fieldValues: Record<string, number | null> = {};
                widget.scoreFields.forEach(field => {
                  const activity = diaryDay.activities.find(a => a.fieldId === field.id);
                  fieldValues[field.id] = activity?.value ?? null;
                });

                return (
                  <ScoreWidget
                    key={widget.id}
                    id={widget.id}
                    name={widget.name || 'Score Widget'}
                    scoreFields={widget.scoreFields}
                    fieldValues={fieldValues}
                    onFieldValueChange={onScoreWidgetFieldChange}
                    isLoading={isLoading}
                  />
                );
              })}
          </div>
        )}

        {/* Notes widget */}
        {onNoteChange && (
          <div className="card shadow-sm diary-category-card mb-3">
            <div className="card-header">
              <h5 className="card-title mb-0">Notes</h5>
            </div>
            <div className="card-body">
              <textarea
                className="form-control"
                rows={4}
                placeholder="Write your notes for this day..."
                value={noteValue}
                onChange={(e) => setNoteValue(e.target.value)}
              />
            </div>
          </div>
        )}

        {isLoading && (
          <div className="diary-day-page__loading-overlay">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}
      </div>

      {/* FieldSet selection modal */}
      {modalFieldSet && (
        <FieldSetModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setModalFieldSet(null);
          }}
          fieldSet={modalFieldSet}
          activities={activitiesByFieldId}
          onFieldClick={onFieldSetClick}
          iconBaseUrl={iconBaseUrl}
        />
      )}

      {/* NumericFieldSet selection modal */}
      {modalNumericFieldSet && onNumericFieldSetValueChange && (
        <NumericFieldSetModal
          isOpen={isNumericModalOpen}
          onClose={() => {
            setIsNumericModalOpen(false);
            setModalNumericFieldSet(null);
          }}
          numericFieldSet={modalNumericFieldSet}
          currentValue={getNumericFieldSetActivity(modalNumericFieldSet.id)?.value ?? null}
          onLabelClick={(labelId, value) => {
            onNumericFieldSetValueChange(modalNumericFieldSet.id, value, labelId);
          }}
          onSliderChange={(value) => {
            onNumericFieldSetValueChange(modalNumericFieldSet.id, value);
          }}
        />
      )}
    </>
  );
}

export default DiaryDayPage;
